import React, { useState } from 'react';
import axios from 'axios';
import { Star, Camera, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Textarea } from '../ui/textarea';

const ReviewModalComponent = ({
  showReviewModal,
  setShowReviewModal,
  reviewData,
  setReviewData,
  completedQuickCut,
  setCompletedQuickCut,
  setQuickCutStatus,
  setMatchedBarber,
  setSuccess,
  loadClientHistory,
  loadClientReviews,
  setError,
  loading,
  setLoading,
  BACKEND_URL,
  uploadImageToImageKit
}) => {
  const [uploadingImages, setUploadingImages] = useState(false);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const uploadedImages = [];

    for (const file of files) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          uploadedImages.push({ file, preview: reader.result });
          if (uploadedImages.length === files.length) {
            setReviewData({ ...reviewData, images: uploadedImages });
            setUploadingImages(false);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
  };

  const handleSubmitReview = async () => {
    if (reviewData.rating === 0) {
      setError('Por favor selecciona una calificación');
      return;
    }

    try {
      setLoading(true);

      const barbershopId = completedQuickCut?.barber?.barbershop_id ||
        completedQuickCut?.barbershop?.id ||
        completedQuickCut?.barbershop_id;

      if (!barbershopId) {
        setError('No se pudo identificar la barbería para la reseña');
        return;
      }

      let uploadedUrls = [];
      const files = reviewData.images.map((img) => img.file).filter(Boolean);

      if (files.length > 0) {
        try {
          uploadedUrls = await Promise.all(
            files.map((file) => uploadImageToImageKit(file, '/reviews'))
          );
        } catch (imageKitError) {
          console.warn('ImageKit upload failed, fallback to local upload:', imageKitError);
        }
      }

      const response = await axios.post(`${BACKEND_URL}/api/reviews`, {
        barbershop_id: barbershopId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        images: uploadedUrls
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (files.length > 0 && uploadedUrls.length === 0) {
        try {
          for (const file of files) {
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(`${BACKEND_URL}/api/reviews/${response.data.id}/upload-image`, formData, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data'
              }
            });
          }
        } catch (uploadError) {
          console.error('Fallback upload failed:', uploadError);
          setError('No se pudo subir las imágenes de la reseña.');
        }
      }

      setShowReviewModal(false);
      setQuickCutStatus('idle');
      setMatchedBarber(null);
      setCompletedQuickCut(null);
      setReviewData({ rating: 0, comment: '', images: [] });
      setSuccess('¡Gracias por tu reseña! El corte se ha agregado a tu historial.');

      loadClientHistory();
      loadClientReviews();
    } catch (error) {
      console.error('Review error:', error);
      const errorMsg = error.response?.data?.detail || 'Error al enviar la reseña';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">¿Cómo estuvo tu corte?</DialogTitle>
          <DialogDescription className="sr-only">
            Completa la calificación, comentario y fotos opcionales de tu corte.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-gray-800 p-3 rounded-lg">
            <p className="text-white font-medium">{completedQuickCut?.barber?.name}</p>
            <p className="text-gray-400 text-sm">{completedQuickCut?.service}</p>
            <p className="text-amber-400 text-sm">${completedQuickCut?.price?.toLocaleString()}</p>
          </div>

          <div>
            <label className="text-white text-sm font-medium mb-2 block">Calificación *</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setReviewData({ ...reviewData, rating: star })}
                  className="text-2xl hover:scale-110 transition-transform"
                >
                  <Star
                    className={star <= reviewData.rating ? "fill-amber-400 text-amber-400" : "text-gray-400"}
                  />
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {reviewData.rating === 0 && "Selecciona tu calificación"}
              {reviewData.rating === 1 && "Muy malo"}
              {reviewData.rating === 2 && "Malo"}
              {reviewData.rating === 3 && "Regular"}
              {reviewData.rating === 4 && "Bueno"}
              {reviewData.rating === 5 && "Excelente"}
            </p>
          </div>

          <div>
            <label className="text-white text-sm font-medium mb-2 block">Comentario (opcional)</label>
            <Textarea
              value={reviewData.comment}
              onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              placeholder="Cuéntanos cómo estuvo tu experiencia..."
              rows={3}
            />
          </div>

          <div>
            <label className="text-white text-sm font-medium mb-2 block">Fotos (opcional)</label>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="bg-gray-800 border-gray-700 text-white file:bg-amber-500 file:text-white file:border-0 file:rounded file:px-3 file:py-1"
                  disabled={uploadingImages}
                />
                <Camera className="w-5 h-5 text-gray-400" />
              </div>

              {reviewData.images.length > 0 && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {reviewData.images.map((img, index) => (
                      <div key={index} className="relative">
                        <img
                          src={img.preview}
                          alt={`Review ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-16 object-cover rounded border border-gray-600"
                        />
                        <Button
                          onClick={() => {
                            const newImages = reviewData.images.filter((_, i) => i !== index);
                            setReviewData({ ...reviewData, images: newImages });
                          }}
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="bg-blue-900/20 border border-blue-700 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-300 text-sm font-medium">
                          {reviewData.images.length} foto{reviewData.images.length !== 1 ? 's' : ''} seleccionada{reviewData.images.length !== 1 ? 's' : ''}
                        </p>
                        <p className="text-blue-400 text-xs">Las fotos se subirán al enviar la reseña</p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSubmitReview}
              disabled={reviewData.rating === 0 || loading}
              className="flex-1 bg-amber-500 hover:bg-amber-700"
            >
              {loading ? 'Enviando...' : 'Enviar Reseña'}
            </Button>
            <Button
              onClick={() => {
                setShowReviewModal(false);
                setQuickCutStatus('idle');
                setMatchedBarber(null);
                setCompletedQuickCut(null);
              }}
              variant="outline"
              className="flex-1 text-white border-gray-600"
            >
              Omitir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ReviewModal = React.memo(ReviewModalComponent);
